import { useEffect } from 'react';
import { usePermissions } from '@/hooks/usePermissions';
import { showInstallPrompt, isAppInstalled } from '@/utils/registerServiceWorker';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/**
 * PWA Install Prompt Component
 * Shows the "Install Dough Man OMS" prompt only to admin users
 * Prevents customers from seeing the install popup
 */
export const PWAInstallPrompt = () => {
  const { isFullAdmin } = usePermissions();

  useEffect(() => {
    // Only set up install prompt handler if user is admin
    if (!isFullAdmin) {
      return;
    }

    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      console.log('💾 Install prompt available (admin only)');

      // Check if suppressed (24h)
      const suppressUntil = localStorage.getItem('pwa-suppress-until');
      const isSuppressed = suppressUntil && Date.now() < parseInt(suppressUntil);

      // Show install prompt after 10 seconds if not already installed and not suppressed
      if (!isAppInstalled() && !isSuppressed) {
        setTimeout(() => {
          showInstallPrompt(e);
        }, 10000);
      }
    };

    const handleAppInstalled = () => {
      console.log('✅ PWA installed successfully');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isFullAdmin]);

  // This component doesn't render anything - it only manages the PWA install prompt
  return null;
};
